import type { CodeBook } from "@king-ict/eupisi-ui/models";
import type { CodeBookPagedFilter, OibServiceResult } from "../../../../models";
import type {
  CommitteeEmployee,
  CommitteeType,
  OibFullInfoEmployee,
} from "../../../../models/Committee";
import { useEffect, useState, type ChangeEvent } from "react";
import { canEmployeeBeRemoved } from "../../utils";
import type { SelectEvent } from "@king-ict/eupisi-ui/components";
import { EMPTY_OIB_FULL_INFO_EMPLOYEE, useCommitteeModalFetching } from ".";

interface IProps {
  committeeType: CommitteeType;
  editingEmployee: CommitteeEmployee | null;
}

export const useCommitteeModal = ({
  committeeType,
  editingEmployee,
}: IProps) => {
  const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);
  const [oibEmployeeServiceResult, setOibEmployeeServiceResult] =
    useState<OibServiceResult<OibFullInfoEmployee> | null>(null);
  const [searchOibEmployee, setSearchOibEmployee] = useState(
    EMPTY_OIB_FULL_INFO_EMPLOYEE
  );
  const [isEditable, setIsEditable] = useState(false);
  const [employee, setEmployee] = useState(editingEmployee);
  const [editedActiveTo, setEditedActiveTo] = useState<Date | null>(null);
  const [showOibAlert, setShowOibAlert] = useState(false);

  const { getEmployees, getOibEmployee } = useCommitteeModalFetching({
    committeeType,
  });

  useEffect(() => {
    setEmployee(editingEmployee);

    if (editingEmployee?.deactivationDate) {
      setEditedActiveTo(new Date(editingEmployee.deactivationDate));
    } else {
      setEditedActiveTo(null);
    }

    setIsEditable(!editingEmployee?.employee && !editingEmployee?.oibEmployee);
  }, [editingEmployee]);

  useEffect(() => {
    setShowOibAlert(!!oibEmployeeServiceResult);
  }, [oibEmployeeServiceResult]);

  const resetModal = () => {
    setFilteredEmployees([]);
    setOibEmployeeServiceResult(null);
    setSearchOibEmployee(EMPTY_OIB_FULL_INFO_EMPLOYEE);
    setEmployee(null);
    setEditedActiveTo(null);
  };

  const handleOibSystemClick = async (oibEmployee: OibFullInfoEmployee) => {
    if (oibEmployee) {
      const result = await getOibEmployee(oibEmployee);
      setOibEmployeeServiceResult(result ?? null);

      if (result?.item?.oib) {
        if (!employee) return;
        const oibEmployee = { ...employee, oibEmployee: result.item };
        setEmployee(oibEmployee);
        setIsEditable(false);
      }
    }
  };

  const handleEmployeeFilterChange = async (filter: string) => {
    const search: CodeBookPagedFilter = {
      pageSize: 100,
      page: 1,
      search: filter,
    };

    const result = await getEmployees(search);
    const employees = result?.results;

    setFilteredEmployees(employees ?? []);
  };

  const handleDateOnChange = (date: Date | null) => {
    setEditedActiveTo(date);
    if (!employee) return;
    const employeeWithDate = { ...employee, deactivationDate: date };
    setEmployee(employeeWithDate);
  };

  const handleEmployeeChangeMultiselect = (e: ChangeEvent<SelectEvent>) => {
    if (!employee) return;
    if (!canEmployeeBeRemoved(editedActiveTo, employee)) return;

    const employeeList = e.target.value as CodeBook[];
    const employeeCodeBook =
      employeeList.length > 0 ? employeeList[0] : undefined;

    const newEmployee = {
      ...employee,
      employee: employeeCodeBook,
      oibEmployee: null,
    };

    setEmployee(newEmployee);
    if (!employeeCodeBook) {
      setIsEditable(true);
      return;
    }
    setIsEditable(false);
    setOibEmployeeServiceResult(null);
    setSearchOibEmployee(EMPTY_OIB_FULL_INFO_EMPLOYEE);
  };

  const handleSearchOibEmployeeChange = (
    value: string,
    key: keyof OibFullInfoEmployee
  ) => {
    const newSearchOibEmployee: OibFullInfoEmployee = {
      ...searchOibEmployee,
      [key]: value,
    };

    setSearchOibEmployee(newSearchOibEmployee);
  };

  return {
    filteredEmployees,
    oibEmployeeServiceResult,
    searchOibEmployee,
    employee,
    isEditable,
    editedActiveTo,
    showOibAlert,
    handleEmployeeFilterChange,
    handleOibSystemClick,
    handleEmployeeChangeMultiselect,
    resetModal,
    handleDateOnChange,
    handleSearchOibEmployeeChange,
  };
};
